import { useEffect, useRef } from "react";
import { VOICE_ATTENTION_START_EVENT } from "./use-voice-attention";

/**
 * Fire `onTap` when the user presses-and-releases Shift alone within
 * 400ms, with no other key pressed during the press and no Cmd/Ctrl/Alt
 * modifier held when Shift went down.
 *
 * Used to toggle screenshot intent live during text composition or
 * voice recording without claiming a chord like Shift+Cmd+S.
 */
export function useShiftTap(
  onTap: () => void,
  options: { enabled?: boolean } = {},
): void {
  const enabled = options.enabled ?? true;
  const onTapRef = useRef(onTap);
  onTapRef.current = onTap;

  useEffect(() => {
    if (!enabled) return;
    let shiftDownAt: number | null = null;
    let otherKeyFiredDuringPress = false;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Shift") {
        if (e.repeat) return;
        if (e.metaKey || e.ctrlKey || e.altKey) return;
        shiftDownAt = Date.now();
        otherKeyFiredDuringPress = false;
        return;
      }
      if (shiftDownAt !== null) otherKeyFiredDuringPress = true;
    };

    const onKeyUp = (e: KeyboardEvent) => {
      if (e.key !== "Shift") return;
      const downAt = shiftDownAt;
      const fired = otherKeyFiredDuringPress;
      shiftDownAt = null;
      otherKeyFiredDuringPress = false;
      if (downAt === null) return;
      if (fired) return;
      if (Date.now() - downAt >= 400) return;
      onTapRef.current();
    };

    const cancelForDrawing = () => {
      if (shiftDownAt !== null) otherKeyFiredDuringPress = true;
    };

    window.addEventListener("keydown", onKeyDown, true);
    window.addEventListener("keyup", onKeyUp, true);
    window.addEventListener(VOICE_ATTENTION_START_EVENT, cancelForDrawing);
    return () => {
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("keyup", onKeyUp, true);
      window.removeEventListener(VOICE_ATTENTION_START_EVENT, cancelForDrawing);
    };
  }, [enabled]);
}
